import React, { useState } from 'react';
import { api } from './api';
import { clock } from './format';

const BROKERS = [
  { key: 'fidelity', label: 'fidelity', note: 'read-only' },
  { key: 'etrade', label: 'e*trade', note: '' },
  { key: 'chase', label: 'chase', note: 'J.P. Morgan self-directed · read-only' },
  { key: 'schwab', label: 'schwab', note: '' },
  { key: 'other', label: 'other…', note: 'pick any broker on the SnapTrade portal' },
];

// Broker connections: list, add, reconnect, remove. The E*TRADE *direct* path
// (verifier code) is here too, hidden behind a small "direct" link, for the day
// SnapTrade's E*TRADE connection turns out not to hold.
export default function Links({ links, onChange, sharing = false, onSharing, canShare = false, me, onMe, onHistory }) {
  const [keysOpen, setKeysOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [direct, setDirect] = useState(null); // { link_id } while an E*TRADE handshake is open
  const [verifier, setVerifier] = useState('');

  const run = async (key, fn) => {
    setBusy(key); setError('');
    try { await fn(); } catch (e) { setError(e.message); }
    setBusy('');
  };

  const linkSnapTrade = broker => run(broker, async () => {
    const { url } = await api('/links/snaptrade', { method: 'POST', body: { broker } });
    window.location.assign(url); // SnapTrade brings them back with ?df=linked
  });
  const reconnect = link => run(`re${link.id}`, async () => {
    const { url } = await api(`/links/snaptrade/${link.id}/reconnect`, { method: 'POST' });
    window.location.assign(url);
  });
  const remove = link => run(`rm${link.id}`, async () => {
    if (!window.confirm(`remove ${link.label}? snapshots go with it.`)) return;
    await api(`/links/${link.id}`, { method: 'DELETE' });
    onChange();
  });
  const sync = link => run(`sy${link.id}`, async () => {
    await api(`/links/${link.id}/sync`, { method: 'POST' });
    setTimeout(onChange, 4000);
  });
  const saveKeys = () => run('keys', async () => {
    onMe(await api('/me/snaptrade', { method: 'PUT', body: { client_id: clientId, consumer_key: consumerKey } }));
    setKeysOpen(false); setClientId(''); setConsumerKey('');
  });
  const etradeStart = () => run('etrade-direct', async () => {
    const { url, link_id } = await api('/links/etrade/start', { method: 'POST' });
    setDirect({ link_id });
    window.open(url, '_blank', 'noopener');
  });
  const etradeFinish = () => run('etrade-finish', async () => {
    await api('/links/etrade/finish', { method: 'POST', body: { link_id: direct.link_id, verifier } });
    setDirect(null); setVerifier('');
    onChange();
  });

  return (
    <section className="df-links">
      {links.length === 0 && (
        <p className="df-muted">connect a brokerage to start. you sign in on the broker's own site, once; we only ever read.</p>
      )}
      {links.length > 0 && (
        <ul className="df-rows">
          {links.map(l => (
            <li key={l.id} className="df-link">
              <span className={`df-dot ${l.status}`} />
              <span>{l.label}</span>
              <span className="df-muted">
                {l.status === 'active' && `synced ${clock(l.last_synced_at)}`}
                {l.status === 'expired' && 'expired — reconnect'}
                {l.status === 'broken' && 'connection broken — reconnect'}
                {l.status === 'pending' && 'not finished'}
              </span>
              <span className="df-actions">
                {(l.status === 'expired' || l.status === 'broken') && l.provider === 'snaptrade' && (
                  <button onClick={() => reconnect(l)} disabled={!!busy}>reconnect</button>
                )}
                {(l.status === 'expired' || l.status === 'broken') && l.provider === 'etrade' && (
                  <button onClick={etradeStart} disabled={!!busy}>re-auth</button>
                )}
                {l.status === 'active' && <button onClick={() => sync(l)} disabled={!!busy}>sync</button>}
                <button onClick={() => remove(l)} disabled={!!busy}>remove</button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="df-add">
        <span className="df-muted">link</span>
        {BROKERS.map(b => (
          <button key={b.key} className="df-btn" onClick={() => linkSnapTrade(b.key)} disabled={!!busy} title={b.note}>
            {busy === b.key ? '…' : b.label}
          </button>
        ))}
        <button className="df-text" onClick={etradeStart} disabled={!!busy} title="E*TRADE's own API; token expires nightly">
          e*trade direct
        </button>
      </div>

      {me && (
        <div className="df-keys">
          <span className="df-muted">snaptrade</span>
          {me.snaptrade === 'own' ? (
            <span>your keys <span className="df-mono df-muted">{me.snaptrade_client_id}</span></span>
          ) : (
            <span className="df-muted">demo data</span>
          )}
          <button className="df-text" onClick={() => setKeysOpen(o => !o)} disabled={!!busy}>{keysOpen ? 'cancel' : 'replace'}</button>
          {keysOpen && (
            <form className="df-connect" onSubmit={e => { e.preventDefault(); saveKeys(); }} autoComplete="off">
              <p className="df-muted">
                <a href="https://dashboard.snaptrade.com" target="_blank" rel="noreferrer">dashboard.snaptrade.com</a> → API keys.
                brokers you link after this use the new keys; existing links keep the old ones.
              </p>
              <label><span>client id</span><input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="PERS-…" spellCheck="false" autoFocus /></label>
              <label><span>key</span><input type="password" value={consumerKey} onChange={e => setConsumerKey(e.target.value)} placeholder="consumer key" /></label>
              <div className="df-add">
                <button className="df-btn" type="submit" disabled={!clientId || !consumerKey || !!busy}>{busy === 'keys' ? 'checking…' : 'save'}</button>
              </div>
            </form>
          )}
        </div>
      )}

      {onSharing && (
        <label className={`df-switch ${canShare ? '' : 'off'}`}>
          <input type="checkbox" checked={sharing} disabled={!canShare} onChange={e => onSharing(e.target.checked)} />
          <span className="df-knob" aria-hidden="true" />
          <span>share my portfolio with everyone on the list</span>
          <span className="df-muted">{canShare ? (sharing ? 'they can see your accounts, read-only' : 'off') : 'link a broker first'}</span>
        </label>
      )}

      {me && onHistory && (
        <label className="df-switch">
          <input type="checkbox" checked={!!me.keep_history} onChange={e => onHistory(e.target.checked)} />
          <span className="df-knob" aria-hidden="true" />
          <span>keep portfolio history</span>
          <span className="df-muted">{me.keep_history ? 'every sync is stored — value chart, look-back' : 'only the latest sync is stored; older snapshots are deleted'}</span>
        </label>
      )}

      {direct && (
        <form className="df-connect" onSubmit={e => { e.preventDefault(); etradeFinish(); }}>
          <p className="df-muted">approve in the e*trade tab, then paste the code it shows</p>
          <label><span>code</span><input value={verifier} onChange={e => setVerifier(e.target.value)} autoFocus spellCheck="false" /></label>
          <div className="df-add">
            <button className="df-btn" type="submit" disabled={!verifier || !!busy}>finish</button>
            <button className="df-text" type="button" onClick={() => setDirect(null)}>cancel</button>
          </div>
        </form>
      )}

      {error && <p className="df-error">{error}</p>}
    </section>
  );
}
